import type { StatusVariant } from '@/ui/StatusBadge';

/**
 * Tipos da tela de Solicitações do Portal do Credenciado.
 *
 * Sem `docs/MODELO-DE-DADOS.md` para esta tabela ainda (nenhuma existe no
 * banco — ver `comum/contrato.ts`): os campos nascem do protótipo e do bom
 * senso. Quando o módulo ganhar API real, este arquivo é o primeiro a se
 * alinhar ao schema publicado.
 */

/**
 * O que o credenciado pode pedir ao DTP. As três primeiras trocam um recurso
 * (veículo, condutor ou monitor) por outro já cadastrado — só essas exigem o
 * campo "Recurso afetado" no formulário.
 */
export type TipoDeSolicitacao =
  | 'substituicao_veiculo'
  | 'substituicao_condutor'
  | 'substituicao_monitor'
  | 'alteracao_cadastral'
  | 'outro';

export type StatusDaSolicitacao =
  'aberta' | 'em_analise' | 'aprovada' | 'recusada' | 'concluida' | 'cancelada';

/** Tipo do recurso — mesmo domínio usado nas telas "Meus veículos/condutores/monitores". */
export type TipoDeRecurso = 'veiculo' | 'condutor' | 'monitor';

/** Uma opção fixa de veículo, condutor ou monitor, para o select de "Recurso afetado". */
export interface OpcaoDeRecurso {
  id: string;
  tipo: TipoDeRecurso;
  rotulo: string;
}

export interface SolicitacaoDeServico {
  id: string;
  /** Identificador legível mostrado ao credenciado. UUID não se lê por telefone. */
  protocolo: string;
  tipo: TipoDeSolicitacao;
  recurso_afetado_id: string | null;
  /** Rótulo do recurso no momento da abertura — não muda se o recurso for editado depois. */
  recurso_afetado_rotulo: string | null;
  motivo: string;
  anexo_nome: string | null;
  status: StatusDaSolicitacao;
  aberta_em: string;
  /** Quando a solicitação entrou em análise — nulo enquanto ainda está só "aberta" (ou se foi cancelada antes disso). */
  em_analise_em: string | null;
  atualizada_em: string;
  /** Parecer do analista do DTP, preenchido quando a decisão sai. */
  parecer: string | null;
}

export interface FiltroDeSolicitacoes {
  pagina: number;
  tamanho: number;
  busca?: string;
  tipo?: TipoDeSolicitacao;
  status?: StatusDaSolicitacao;
  /** Data inicial do período de abertura, formato ISO (yyyy-mm-dd). */
  criadaDe?: string;
  /** Data final do período de abertura, formato ISO (yyyy-mm-dd). */
  criadaAte?: string;
}

/** O que o formulário de "Nova solicitação" envia. */
export interface NovaSolicitacaoEntrada {
  tipo: TipoDeSolicitacao;
  recurso_afetado_id?: string;
  motivo: string;
  anexo_nome?: string;
}

export const TIPOS_DE_SOLICITACAO: ReadonlyArray<{ value: TipoDeSolicitacao; label: string }> = [
  { value: 'substituicao_veiculo', label: 'Substituição de veículo' },
  { value: 'substituicao_condutor', label: 'Substituição de condutor' },
  { value: 'substituicao_monitor', label: 'Substituição de monitor' },
  { value: 'alteracao_cadastral', label: 'Alteração de dados cadastrais' },
  { value: 'outro', label: 'Outro assunto' },
];

export const STATUS_DA_SOLICITACAO: ReadonlyArray<{ value: StatusDaSolicitacao; label: string }> = [
  { value: 'aberta', label: 'Aberta' },
  { value: 'em_analise', label: 'Em análise' },
  { value: 'aprovada', label: 'Aprovada' },
  { value: 'recusada', label: 'Recusada' },
  { value: 'concluida', label: 'Concluída' },
  { value: 'cancelada', label: 'Cancelada' },
];

const ROTULO_DO_TIPO: Record<TipoDeSolicitacao, string> = Object.fromEntries(
  TIPOS_DE_SOLICITACAO.map((opcao) => [opcao.value, opcao.label]),
) as Record<TipoDeSolicitacao, string>;

const ROTULO_DO_STATUS: Record<StatusDaSolicitacao, string> = Object.fromEntries(
  STATUS_DA_SOLICITACAO.map((opcao) => [opcao.value, opcao.label]),
) as Record<StatusDaSolicitacao, string>;

export function rotuloDoTipo(tipo: TipoDeSolicitacao): string {
  return ROTULO_DO_TIPO[tipo];
}

export function rotuloDoStatus(status: StatusDaSolicitacao): string {
  return ROTULO_DO_STATUS[status];
}

/** Tom do selo de status — mapeado para as variantes que `StatusBadge` já entende. */
export const STATUS_PARA_SELO: Record<StatusDaSolicitacao, StatusVariant> = {
  aberta: 'pendente',
  em_analise: 'em_execucao',
  aprovada: 'aprovado',
  recusada: 'rejeitado',
  concluida: 'concluida',
  cancelada: 'cancelado',
};

/** Só estes três tipos trocam um recurso — os outros dois não mostram o campo. */
export const RECURSO_DO_TIPO: Partial<Record<TipoDeSolicitacao, TipoDeRecurso>> = {
  substituicao_veiculo: 'veiculo',
  substituicao_condutor: 'condutor',
  substituicao_monitor: 'monitor',
};

/** Enquanto a solicitação está aberta ou em análise, o credenciado pode cancelar. */
export function podeCancelar(status: StatusDaSolicitacao): boolean {
  return status === 'aberta' || status === 'em_analise';
}

export interface PassoDaLinhaDoTempo {
  rotulo: string;
  concluido: boolean;
  atual: boolean;
  /** Quando o passo representa um estado terminal negativo (recusada/cancelada). */
  negativo?: boolean;
  /** Data em que o passo foi alcançado — nulo enquanto o passo não foi concluído nem é o atual. */
  data: string | null;
}

/**
 * Linha do tempo simples: aberta → em análise → decisão. "Decisão" mostra o
 * resultado (aprovada/recusada/concluída/cancelada) quando a solicitação sai
 * do fluxo normal.
 */
export function passosDaLinhaDoTempo(item: SolicitacaoDeServico): PassoDaLinhaDoTempo[] {
  const status = item.status;
  const cancelada = status === 'cancelada';
  const decidida = status === 'aprovada' || status === 'recusada' || status === 'concluida';
  const emAnaliseConcluida = status !== 'aberta' && !cancelada;

  const rotuloDecisao =
    status === 'recusada'
      ? 'Recusada'
      : status === 'aprovada'
        ? 'Aprovada'
        : status === 'concluida'
          ? 'Concluída'
          : 'Decisão';

  return [
    { rotulo: 'Aberta', concluido: true, atual: status === 'aberta', data: item.aberta_em },
    {
      rotulo: 'Em análise',
      concluido: emAnaliseConcluida,
      atual: status === 'em_analise',
      data: emAnaliseConcluida ? item.em_analise_em : null,
    },
    {
      rotulo: cancelada ? 'Cancelada' : rotuloDecisao,
      concluido: decidida || cancelada,
      atual: decidida || cancelada,
      negativo: status === 'recusada' || cancelada,
      data: decidida || cancelada ? item.atualizada_em : null,
    },
  ];
}

/** Formata uma data ISO como "dd/MM/yyyy às HH:mm", para exibir junto de cada passo concluído. */
export function formatarDataHora(dataIso: string): string {
  const data = new Date(dataIso);
  const dataFormatada = data.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const horaFormatada = data.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${dataFormatada} às ${horaFormatada}`;
}
